import { useEffect, useMemo } from 'react';

import { PlayerMarketDataFragment, useNotifyOnMarketForEventsSubscription } from '@/api/events/query.generated';
import { EventInfo, EventInfoWithoutPlayers } from '@/api/events/types/types';
import { useSubscribeEventsMatchUpdate } from '@/feature/bets-sbk/hooks/use-subscribe-events-match-update';
import { recordInvocation } from '@/feature/devsettings/utils/invocations-tracker';
import { useAuthUserSettings } from '@/hooks/use-auth-user-settings';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useStableIds } from '@/hooks/use-stable-ids';
import { useUserStore } from '@/hooks/use-user-store';
import { EventStatus } from '@/types/api.generated';
import { logger } from '@/utils/logging';
import { areProjectionsMatching } from '@/utils/projections';

import { BetslipPick, PlayerProjection } from '../types';
import { ProjectionUpdateStatus, getProjectionUpdateStatusFromProjections } from '../utils/betslip-projection-changed';
import { createPlayerEventKey } from '../utils/markets-cache-store-utils';
import { useBetslipActions } from './use-betslip-actions';
import { useBetslipStore } from './use-betslip-store';
import { usePlayerPropsStore } from './use-player-props-store';

const LIVE_LOG_TAG = '[LIVE]';

/**
 * Connects to web-socket updates for the given event ID, and updates the player props store with new markets
 * while also triggering betslip updates if projections have changed for the betslip
 * Also subscribes to the Ably WS using the PAM event IDs, to get live updates about the game clock
 */
export const useLiveEventsPropsUpdate = ({
    events,
    options,
}: {
    events: (EventInfo | EventInfoWithoutPlayers)[] | undefined;
    options: { subscribeToMarketUpdates: boolean; subscribeToGameClockUpdates: boolean; debugTag: string };
}) => {
    // Check Prismic feature flag to determine if we should enable websocket subscriptions
    const liveWebsocketEnabled = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.settings?.enable_live_market_websockets ?? false
    );

    const liveGameClockEnabled = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.settings?.enable_live_game_periods ?? false
    );

    const guest = useUserStore(state => state.guest);
    const profile = useUserStore(state => state.profile);

    const liveEventIds = useMemo(() => {
        return events?.filter(it => it.status === EventStatus.InProgress).map(e => e.id) ?? [];
    }, [events]);

    const rawLivePamEventIds = useMemo<string[]>(() => {
        return (
            events
                ?.filter(it => it.status === EventStatus.InProgress)
                .map(it => it.dataFeedSourceIds.find(i => i.source === 'PAM')?.id)
                .filter(Boolean) ?? []
        );
    }, [events]);

    // Then make it stable with deep comparison
    const livePamEventIds = useStableIds(rawLivePamEventIds);

    // Only subscribe if the feature flags are enabled, we have valid event IDs, and user is not a guest
    const shouldSubscribeToMarketUpdates =
        options.subscribeToMarketUpdates && liveWebsocketEnabled && liveEventIds.length > 0 && !guest;
    const shouldSubscribeToGameClockUpdates =
        options.subscribeToGameClockUpdates && liveGameClockEnabled && livePamEventIds.length > 0 && !guest;

    // Subscribe to Ably to get live match updates (game clock, periods, etc)
    useSubscribeEventsMatchUpdate(livePamEventIds, shouldSubscribeToGameClockUpdates);

    // Only profile invocations if the user is not a guest and the email ends with '@betr.app'
    // To avoid profiling for all users, we only profile for internal users
    const shouldProfileInvocations = profile?.email?.endsWith('@betr.app');

    // Subscribe to GraphQL web sockets to get live market updates
    const [{ data }] = useNotifyOnMarketForEventsSubscription(
        {
            variables: { eventIds: liveEventIds },
            pause: !shouldSubscribeToMarketUpdates, // Pause the subscription if requirements are not met
        },
        (_, next) => {
            if (shouldProfileInvocations) {
                recordInvocation(`fantasy-ws-${liveEventIds.join('_')}`);
            }
            const newMarkets = next?.notifyOnMarketForEvents;
            if (newMarkets?.length && newMarkets.length > 0) {
                //Uncomment to 'debug' / see updates
                // newMarkets[0].markets[0].marketStatus = MarketStatus.Suspended;
                // newMarkets[0].markets[0].value = 17;
                // newMarkets[0].markets[0].currentValue = 12;
            }
            return next;
        }
    );

    useUpdatePlayerStoreWithNewMarkets(data?.notifyOnMarketForEvents);
};

/**
 * @internal EXPORTED ONLY FOR TESTING PURPOSES. DO NOT USE DIRECTLY, instead use `useLiveEventsPropsUpdate`
 *
 * Hook that will update the player projections store cache with the new player market data from web-sockets.
 * This will also run some checks to see if projections have changed for the current betslip picks
 * @param data - the newly fetched data that contains the player projections
 */
export const useUpdatePlayerStoreWithNewMarkets = (data: PlayerMarketDataFragment[] | undefined) => {
    const { syncPicks } = useBetslipActions();

    const { data: userData } = useAuthUserSettings();
    const acceptAllProjectionsChanges = userData && userData.accept_all_odds_changes;
    useEffect(() => {
        if (data) {
            //Update first the player-props store
            updatePlayerPropsStoreWithNewMarkets(data);

            //Update also the betslip store
            const currentPicks = useBetslipStore.getState().betslip ?? [];
            const updateBetslipProjection = useBetslipStore.getState().actions.updateBetslipProjection;

            //Since we are passing here only 'data' as new markets received form the WS
            //this result will contain 'removed' projections - but this is false, they are just not found ('removed')
            //because WS data only sends new / updated projections
            //So we are going to ignore in this flow the projections with status 'removed'
            const betslipChangedPicks = getBetslipChangedPicks(currentPicks, data);

            if (betslipChangedPicks.length > 0) {
                //Picks that have value changes,type changes or market suspended/reopened must be udpated in the betslip as well
                const valueChangedPicks = betslipChangedPicks.filter(it =>
                    ['value-changed', 'type-changed', 'suspended', 'resumed'].includes(it.status.status)
                );
                const suspendedAndResumedPicks = betslipChangedPicks.filter(it =>
                    ['suspended', 'resumed'].includes(it.status.status)
                );

                const marketIdChangedPicks = betslipChangedPicks.filter(it => it.status.status === 'market-id-change');
                if (marketIdChangedPicks.length > 0) {
                    marketIdChangedPicks.forEach(pick => {
                        const betslipPick = currentPicks.find(p => p.player.id === pick.playerId);
                        logger.debug(
                            LIVE_LOG_TAG,
                            'Betslip market ID changed for pick, updating betslip projection:',
                            `${betslipPick?.player.firstName} ${betslipPick?.player.lastName}`
                        );
                        updateBetslipProjection(pick.playerId, pick.status.projection);
                    });
                }
                if (valueChangedPicks.length > 0) {
                    logger.debug(LIVE_LOG_TAG, 'Betslip changed picks:', valueChangedPicks);
                    if (acceptAllProjectionsChanges) {
                        valueChangedPicks.forEach(pick => {
                            updateBetslipProjection(pick.playerId, pick.status.projection);
                        });
                    } else {
                        // For suspended and resumed picks, we want to update the betslip projections here,
                        // because the modal for accept projections changes won't show in sync picks
                        // because we want to keep the suspended picks in the betslip without asking the user
                        suspendedAndResumedPicks.forEach(pick => {
                            updateBetslipProjection(pick.playerId, pick.status.projection);
                        });
                        // Sync picks to take new event data and show the projections changes modal
                        syncPicks({ expectError: false });
                    }
                }
            }
        }
    }, [acceptAllProjectionsChanges, data, syncPicks]);
};

/**
 * Updates the player props store with new market data from web-socket updates
 */
const updatePlayerPropsStoreWithNewMarkets = (markets: PlayerMarketDataFragment[]) => {
    const cachedMarketsByPlayerAndEvent = usePlayerPropsStore.getState()._playerMarkets;
    const updateStorePlayersProjections = usePlayerPropsStore.getState().actions.updateStorePlayersProjections;

    /*
        cachedMarketsByPlayerAndEvent:
        [
        {'event1-player1': [{...market1}, {...market2}]},
        {'event1-player2': [{...market3}, {...market4}]},
        {'event2-player1': [{...market5}, {...market6}, {...market7}]},
        ]

        newMarketsByPlayerAndEvent:
        [
        {'event1-player1': [{...NEWmarket2}, {Complete_NEW_market}]},
        {'event2-player1': [{...NEWmarket6}, {...NEWmarket7}]},
        ]

        new playerMarkets:
        [
        {'event1-player1': [{...market1}, {...NEWmarket2}, {Complete_NEW_market}]},
        {'event1-player2': [{...market3}, {...market4}]},
        {'event2-player1': [{...market5}, {...NEWmarket6}, {...NEWmarket7}]},
        ]
    */

    if (markets.length === 0) {
        return;
    }
    // transform PlayerMarket[] to Record<string, PlayerProjection[]>
    const newMarketsByPlayerAndEvent = markets.reduce((acc, market) => {
        if (market.markets) {
            acc[createPlayerEventKey(market.playerId, market.eventId)] = market.markets;
        }
        return acc;
    }, {} as Record<string, PlayerProjection[]>);

    const newMarketsRecord = Object.keys(newMarketsByPlayerAndEvent).reduce((acc, key) => {
        const cachedMarkets = cachedMarketsByPlayerAndEvent[key] ?? [];
        const newMarkets = newMarketsByPlayerAndEvent[key] ?? [];

        // Merge existing markets with updates and add completely new markets
        const mergedMarkets = [...cachedMarkets];

        newMarkets.forEach(newMarket => {
            const existingIndex = mergedMarkets.findIndex(market => areProjectionsMatching(market, newMarket));

            if (existingIndex >= 0) {
                // Update existing market
                const previourMarket = mergedMarkets[existingIndex];
                mergedMarkets[existingIndex] = {
                    ...newMarket,
                    //Update current value only if it's not null, otherwise use the previous value
                    //This is to avoid null values from the WS to overwrite the current values, as there is a BE bug that sends null values
                    //TODO: maybe we can avoid / delete this when the BE bug is fixed
                    currentValue: newMarket.currentValue ?? previourMarket.currentValue,
                };
            } else {
                // Add completely new market
                mergedMarkets.push(newMarket);
            }
        });

        acc[key] = mergedMarkets;
        return acc;
    }, {} as Record<string, PlayerProjection[]>);

    logger.debug(LIVE_LOG_TAG, 'New Markets:', newMarketsRecord);
    updateStorePlayersProjections(newMarketsRecord);
};

/**
 * Finds the changed projection from betslip, based on new markets received via web-sockets
 */
const getBetslipChangedPicks = (
    currentPicks: BetslipPick[],
    newMarkets: PlayerMarketDataFragment[]
): { playerId: string; status: ProjectionUpdateStatus }[] => {
    // transform PlayerMarket[] to Record<string, PlayerProjection[]> using playerId + eventId keys
    const newMarketsByPlayerAndEvent = newMarkets.reduce((acc, market) => {
        if (market.markets) {
            acc[createPlayerEventKey(market.playerId, market.eventId)] = market.markets;
        }
        return acc;
    }, {} as Record<string, PlayerProjection[]>);

    // Get the changed projection for the player from the current picks
    const changedProjectionForPlayer = currentPicks
        .filter(pick => newMarketsByPlayerAndEvent[createPlayerEventKey(pick.player.id, pick.eventId)])
        .map(pick => {
            const playerMarkets = newMarketsByPlayerAndEvent[createPlayerEventKey(pick.player.id, pick.eventId)];
            const changedProjectionStatus = getProjectionUpdateStatusFromProjections(pick, playerMarkets);
            return {
                pick,
                changedProjectionStatus,
            };
        });

    return changedProjectionForPlayer.map(it => ({ playerId: it.pick.player.id, status: it.changedProjectionStatus }));
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logNewMarkets = (newMarkets: PlayerMarketDataFragment[]) => {
    if (__DEV__) {
        newMarkets.forEach(playerMarkets => {
            playerMarkets.markets?.forEach(market => {
                console.log(
                    LIVE_LOG_TAG,
                    'New market for:',
                    playerMarkets.playerId,
                    market.name,
                    market.value,
                    market.marketStatus
                );
            });
        });
    }
};
